<script setup lang="ts">
import { scrollLock } from "@magnit/core/src/helpers/scrollLock";
import type { IStatus } from "~/typings/api/status";
import { logRequestError } from "~/utils/logger";

const props = defineProps<{
  searchValue: string;
  isOpenSuggest: boolean;
}>();

const emit = defineEmits<{
  "click:suggest": [value: string];
  "update:isOpenSuggest": [value: boolean];
  "update:searchValue": [value: string];
}>();

const MIN_SYMBOLS_FOR_SEARCH = 3;

const suggests = ref<string[]>([]);
const popularSuggests = ref<string[]>([]);
const status = ref<IStatus>("initial");
const statusPopular = ref<IStatus>("initial");
const showResults = computed(() => suggests.value.length && props.searchValue);
const showPopular = computed(
  () =>
    props.searchValue.length < MIN_SYMBOLS_FOR_SEARCH &&
    statusPopular.value === "success",
);
const { requestSearchSuggestions, requestPopularSearchSuggestions } =
  searchSuggestionsApi();

const closeSuggests = () => {
  if (!props.isOpenSuggest) return;
  emit("update:isOpenSuggest", false);
  scrollLock.disable();
};

const clearSuggests = (withError?: boolean) => {
  suggests.value = [];
  status.value = withError ? "error" : "initial";
};

const onClickSuggest = (searchValue: string) => {
  emit("update:searchValue", searchValue);
  emit("click:suggest", searchValue);
  closeSuggests();
};
const checkSearchValue = () => {
  if (props.searchValue?.length >= MIN_SYMBOLS_FOR_SEARCH) return true;
  clearSuggests();
};

const fetchSuggests = async () => {
  if (!checkSearchValue()) return;
  try {
    status.value = "pending";
    const response = await requestSearchSuggestions(props.searchValue);
    suggests.value =
      response.blocks.find((block) => block.type === "TextSuggestionsBlock")
        ?.textSuggestions || [];
  } catch (err) {
    logRequestError("Ошибка при получении поисковых предложений", err);
    clearSuggests(true);
    status.value = "error";
  }
  status.value = "success";
};

const fetchPopularSuggests = async () => {
  try {
    statusPopular.value = "pending";
    const response = await requestPopularSearchSuggestions();
    popularSuggests.value =
      response.blocks.find((block) => block.type === "PopularSuggestionsBlock")
        ?.popularSuggestions || [];
  } catch (err) {
    logRequestError(
      "Ошибка при получении популярных поисковых предложений",
      err,
    );
    statusPopular.value = "error";
  }
  statusPopular.value = "success";
};

watchDebounced(
  () => props.searchValue,
  () => fetchSuggests(),
  { debounce: 400 },
);

onMounted(() => {
  fetchPopularSuggests();
});
</script>

<template>
  <div class="suggests">
    <VWrapperLocal>
      <ClientOnly>
        <AppSearchSuggestHistory
          :search-value="searchValue"
          :is-open-suggest="isOpenSuggest"
          @click:suggest="onClickSuggest"
        />
      </ClientOnly>
      <AppSearchSuggestPopular
        v-if="showPopular"
        :items="popularSuggests"
        @click:suggest="onClickSuggest"
      />
      <AppSearchSuggestResults
        v-if="showResults"
        :items="suggests"
        @click:suggest="onClickSuggest"
      />
    </VWrapperLocal>
  </div>
</template>

<style scoped lang="postcss">
.suggests {
  padding-bottom: var(--pl-unit-x6);
}
</style>
